import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Header from './components/Header';
import Home from './components/Home';
import About from './components/About';
import Skills from './components/Skills';
import Education from './components/Education';
import Work from './components/Work';
import Contact from './components/Contact';
import Footer from './components/Footer';
import Projects from './components/Projects';
import ParticleBackground from './components/ParticleBackground';
import ScrollProgress from './components/ScrollProgress';
import './i18n';

const App: React.FC = () => {
  const [currentSection, setCurrentSection] = useState<string>('');

  useEffect(() => {
    const handleScroll = () => {
      const sections = document.querySelectorAll('section[id]');
      
      sections.forEach(section => {
        const sectionTop = (section as HTMLElement).offsetTop;
        const sectionHeight = (section as HTMLElement).offsetHeight;
        const scrollY = window.scrollY;
        
        const sectionId = section.getAttribute('id') || '';
        
        if (scrollY > sectionTop - 200 && scrollY <= sectionTop + sectionHeight - 200) {
          setCurrentSection(sectionId);
        }
      });
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Page transition variants
  const pageVariants = {
    initial: {
      opacity: 0,
      y: 50
    },
    animate: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: [0.6, 0.05, 0.01, 0.9]
      }
    }
  };

  return (
      <motion.div
        key="main"
        initial="initial"
        animate="animate"
        variants={pageVariants}
        className="flex flex-col min-h-screen bg-gray-900 text-white relative"
      >
        <ParticleBackground />
        <ScrollProgress />
        <Header currentSection={currentSection} />
        <main className="flex-grow">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Home />
            <About />
            <Skills />
            <Projects />
            <Education />
            <Work />
            <Contact />
          </div>
        </main>
        <Footer />
      
      <div className="fixed top-0 left-0 w-16 h-16 pointer-events-none z-50">
        <motion.div 
          className="w-full h-full border-t-2 border-l-2 border-primary-500 opacity-50"
          animate={{
            opacity: [0.3, 0.6, 0.3],
            scale: [1, 1.05, 1]
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            repeatType: "reverse"
          }}
        />
      </div>
      <div className="fixed top-0 right-0 w-16 h-16 pointer-events-none z-50">
        <motion.div 
          className="w-full h-full border-t-2 border-r-2 border-accent-500 opacity-50"
          animate={{
            opacity: [0.3, 0.6, 0.3],
            scale: [1, 1.05, 1]
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            repeatType: "reverse",
            delay: 1
          }}
        />
      </div>
      <div className="fixed bottom-0 left-0 w-16 h-16 pointer-events-none z-50">
        <motion.div 
          className="w-full h-full border-b-2 border-l-2 border-accent-500 opacity-50"
          animate={{
            opacity: [0.3, 0.6, 0.3],
            scale: [1, 1.05, 1]
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            repeatType: "reverse",
            delay: 2
          }}
        />
      </div>
      <div className="fixed bottom-0 right-0 w-16 h-16 pointer-events-none z-50">
        <motion.div 
          className="w-full h-full border-b-2 border-r-2 border-primary-500 opacity-50"
          animate={{
            opacity: [0.3, 0.6, 0.3],
            scale: [1, 1.05, 1]
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            repeatType: "reverse",
            delay: 3
          }}
        />
      </div>
    </motion.div>
  );
};

export default App;
